import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../styling/stylingHelper';

type TFn = ReturnType<typeof useTranslation>['t'];

// One reviewable issue — a single card in the walk-through.
export interface ReviewItem {
  id: string;
  title: string;
  subtitle?: string;
  body?: ReactNode;
  actions?: ReactNode;
}

// A group of issues handled together (e.g. one supplier, or "New suppliers").
export interface ReviewBucket {
  id: string;
  title: string;
  hint?: string;
  items: ReviewItem[];
}

// ── Score colour tone ──────────────────────────────────────────────
function toneFor(scorePct: number) {
  if (scorePct >= 98) return { ring: 'text-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', track: 'text-emerald-100 dark:text-emerald-900/30' };
  if (scorePct >= 92) return { ring: 'text-amber-500', text: 'text-amber-600 dark:text-amber-400', track: 'text-amber-100 dark:text-amber-900/30' };
  return { ring: 'text-rose-500', text: 'text-rose-600 dark:text-rose-400', track: 'text-rose-100 dark:text-rose-900/30' };
}

export function ScoreRing({ scorePct }: { scorePct: number }) {
  const tone = toneFor(scorePct);
  const r = 52;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, scorePct));
  const dash = (pct / 100) * circ;
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" className={tone.track} stroke="currentColor" />
        <circle
          cx="60" cy="60" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
          className={cn(tone.ring, 'transition-all duration-700 ease-out')}
          stroke="currentColor"
          strokeDasharray={`${dash} ${circ}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('text-2xl font-bold tabular-nums', tone.text)}>{scorePct.toFixed(1)}%</span>
        <span className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>clean</span>
      </div>
    </div>
  );
}

// ── Scored report + entry point to the walk-through ────────────────
export interface AuditScoreReportProps {
  title: string;
  scorePct: number;
  scannedLabel: string;
  goodLabel: string;
  buckets: ReviewBucket[];
  costLabel?: string;
  fullBreakdown?: ReactNode;
  t: TFn;
}

export function AuditScoreReport({ title, scorePct, scannedLabel, goodLabel, buckets, costLabel, fullBreakdown, t }: AuditScoreReportProps) {
  const liveBuckets = useMemo(() => buckets.filter(b => b.items.length > 0), [buckets]);
  const toHandle = useMemo(() => liveBuckets.reduce((n, b) => n + b.items.length, 0), [liveBuckets]);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [showFull, setShowFull] = useState(false);

  return (
    <div className="space-y-4">
      <div className={cn('rounded-xl p-5 border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30')}>
        <div className="flex items-center gap-5">
          <ScoreRing scorePct={scorePct} />
          <div className="flex-1 min-w-0">
            <h4 className={cn(primaryText, semiBoldText)}>{title}</h4>
            <p className={cn(secondaryText, smallTextSize, 'mt-0.5')}>{scannedLabel}</p>
            <p className={cn('text-emerald-600 dark:text-emerald-400', smallTextSize, semiBoldText, 'mt-2')}>
              ✓ {goodLabel}
            </p>
            {toHandle > 0 ? (
              <button
                onClick={() => setSessionOpen(true)}
                className={cn(
                  'mt-3 px-4 py-2 rounded-lg transition-colors',
                  'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                  smallTextSize, semiBoldText
                )}
              >
                {t('adminTesting.review.startReview')} → {t('adminTesting.review.inBuckets', { count: toHandle, buckets: liveBuckets.length })}
              </button>
            ) : (
              <p className={cn('mt-3', primaryText, smallTextSize, semiBoldText)}>
                🎉 {t('adminTesting.review.allGoodDesc')}
              </p>
            )}
            {costLabel && <p className={cn(secondaryText, 'text-xs font-mono mt-3')}>{costLabel}</p>}
          </div>
        </div>
      </div>

      {fullBreakdown && (
        <div>
          <button
            onClick={() => setShowFull(v => !v)}
            className={cn(secondaryText, 'text-xs underline hover:text-purple-600 dark:hover:text-purple-400')}
          >
            {showFull ? t('adminTesting.review.hideFull') : t('adminTesting.review.showFull')}
          </button>
          {showFull && <div className="mt-3">{fullBreakdown}</div>}
        </div>
      )}

      {sessionOpen && (
        <ReviewSession title={title} buckets={liveBuckets} onClose={() => setSessionOpen(false)} t={t} />
      )}
    </div>
  );
}

// ── Single-card modal: open one issue's full detail card on its own ──
export function ReviewCardModal({ title, item, onClose, t }: { title: string; item: ReviewItem; onClose: () => void; t: TFn }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className={cn(primaryText, semiBoldText, 'min-w-0 truncate')}>{title}</h3>
          <button onClick={onClose} className={cn(secondaryText, 'text-sm hover:text-gray-900 dark:hover:text-gray-100 ml-3')}>✕</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="min-w-0">
              <div className={cn(primaryText, semiBoldText, 'break-words')}>{item.title}</div>
              {item.subtitle && <div className={cn(secondaryText, 'text-xs mt-0.5')}>{item.subtitle}</div>}
            </div>
            {item.body && <div className="mt-3">{item.body}</div>}
            {item.actions && <div className="mt-4 flex items-center gap-2 flex-wrap">{item.actions}</div>}
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button onClick={onClose} className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white', smallTextSize, semiBoldText)}>
            {t('adminTesting.review.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Walk-through: one card at a time, grouped into buckets ─────────
interface QueueEntry {
  bucketId: string;
  bucketTitle: string;
  bucketHint?: string;
  bucketIndex: number;
  itemInBucket: number;
  bucketSize: number;
  item: ReviewItem;
}

export function ReviewSession({ title, buckets, onClose, t }: { title: string; buckets: ReviewBucket[]; onClose: () => void; t: TFn }) {
  const queue = useMemo<QueueEntry[]>(() => {
    const out: QueueEntry[] = [];
    buckets.forEach((b, bi) => {
      b.items.forEach((item, ii) => {
        out.push({ bucketId: b.id, bucketTitle: b.title, bucketHint: b.hint, bucketIndex: bi, itemInBucket: ii + 1, bucketSize: b.items.length, item });
      });
    });
    return out;
  }, [buckets]);

  const [pos, setPos] = useState(0);
  const [handled, setHandled] = useState(0);
  const [skipped, setSkipped] = useState(0);

  const total = queue.length;
  const finished = pos >= total;
  const entry = finished ? null : queue[pos];
  const progressPct = total === 0 ? 100 : (pos / total) * 100;

  const advance = (kind: 'done' | 'skip') => {
    if (kind === 'done') setHandled(n => n + 1);
    else setSkipped(n => n + 1);
    setPos(p => p + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header + progress */}
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className={cn(primaryText, semiBoldText)}>{title}</h3>
            <button onClick={onClose} className={cn(secondaryText, 'text-sm hover:text-gray-900 dark:hover:text-gray-100')}>✕</button>
          </div>
          <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div className="h-full bg-purple-500 dark:bg-purple-400 transition-all duration-300" style={{ width: `${progressPct}%` }} />
          </div>
          {!finished && entry && (
            <div className="flex items-center justify-between mt-2 text-xs">
              <span className={cn('text-purple-600 dark:text-purple-400', semiBoldText)}>
                {t('adminTesting.review.bucketLabel', { current: entry.bucketIndex + 1, total: buckets.length })} · {entry.bucketTitle}
              </span>
              <span className={secondaryText}>{t('adminTesting.review.overallProgress', { current: pos + 1, total })}</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {finished ? (
            <div className="text-center py-10">
              <div className="text-5xl mb-3">🎉</div>
              <h4 className={cn(primaryText, semiBoldText, 'text-lg')}>{t('adminTesting.review.finishedTitle')}</h4>
              <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
                {t('adminTesting.review.finishedDesc', { handled, skipped })}
              </p>
            </div>
          ) : entry && (
            <div>
              {entry.bucketHint && entry.itemInBucket === 1 && (
                <p className={cn(secondaryText, 'text-xs mb-3')}>{entry.bucketHint}</p>
              )}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className={cn(primaryText, semiBoldText, 'break-words')}>{entry.item.title}</div>
                    {entry.item.subtitle && <div className={cn(secondaryText, 'text-xs mt-0.5')}>{entry.item.subtitle}</div>}
                  </div>
                  <span className={cn(secondaryText, 'text-[10px] whitespace-nowrap')}>
                    {entry.itemInBucket}/{entry.bucketSize}
                  </span>
                </div>
                {entry.item.body && <div className="mt-3">{entry.item.body}</div>}
                {entry.item.actions && <div className="mt-4 flex items-center gap-2 flex-wrap">{entry.item.actions}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          {finished ? (
            <button
              onClick={onClose}
              className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white', smallTextSize, semiBoldText)}
            >
              {t('adminTesting.review.close')}
            </button>
          ) : (
            <>
              <button
                onClick={() => advance('skip')}
                className={cn(
                  'px-4 py-2 rounded-lg transition-colors',
                  'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                  primaryText, smallTextSize
                )}
              >
                {t('adminTesting.review.skip')}
              </button>
              <button
                onClick={() => advance('done')}
                className={cn('px-4 py-2 rounded-lg bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-700 text-white dark:text-white', smallTextSize, semiBoldText)}
              >
                ✓ {t('adminTesting.review.done')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
